import { useMemo } from 'react';
import { LineMarkerDefs, LineMarkers } from 'widgets/Lines/LineWidget/LineRenders/LineMarkers';
import {
  DEFAULT_LINE_END_PADDING_CONST,
  LINE_END_PADDING_CONST_MAP,
} from 'widgets/Lines/LineWidget/LineRenders/LineRender.config';
import { getDashArray } from 'widgets/Lines/LineWidget/LineWidget.helpers';

import { useLineIconRender } from '../useLineIconRender';
import { generateLineIconMarkerId, generateLineIconPathId, generateLineSvgId } from './Lines.helpers';
import { LineProps, ResizeLine } from './Lines.types';

const ID_PREFIX = 'quarter-curve-';

/**
 * Compute path considering padding required for start and end arrows
 * @param param0
 * @returns
 */
const computePathWithPadding = ({
  widthPx,
  heightPx,
  strokeWidth,
  startArrowStyle,
  endArrowStyle,
}: Omit<ResizeLine, 'id'>) => {
  const startY = strokeWidth * (LINE_END_PADDING_CONST_MAP[startArrowStyle] ?? DEFAULT_LINE_END_PADDING_CONST);
  const endY = strokeWidth * (LINE_END_PADDING_CONST_MAP[endArrowStyle] ?? DEFAULT_LINE_END_PADDING_CONST);
  return `M ${strokeWidth / 2}, ${startY} Q ${strokeWidth / 2} ${heightPx} ${widthPx - endY} ${
    heightPx - strokeWidth / 2
  }`;
};

/**
 * Compute path without considering padding required for start and end arrows
 * @param param0
 * @returns
 */
const computePath = ({ widthPx, heightPx, strokeWidth }: Pick<ResizeLine, 'widthPx' | 'heightPx' | 'strokeWidth'>) =>
  `M ${strokeWidth / 2}, 0 Q ${strokeWidth / 2} ${heightPx} ${widthPx} ${heightPx - strokeWidth / 2}`;

/**
 * Resize fn to trigger in bounding box resize event
 *
 * @param lineData
 */
export const resizeQuarterCurve = (lineData: ResizeLine) => {
  const { id, widthPx, heightPx } = lineData;
  const svg = document.getElementById(generateLineSvgId(id));
  const svgLine = document.getElementById(generateLineIconPathId(id, ID_PREFIX));

  const markerId = generateLineIconMarkerId(id, ID_PREFIX);
  const markerStart = document.getElementById(`start-arrow-path-${markerId}`);
  const markerEnd = document.getElementById(`end-arrow-path-${markerId}`);

  // Update the path
  if (svg && svgLine && markerStart && markerEnd) {
    svg.setAttribute('width', `${widthPx}px`);
    svg.setAttribute('height', `${heightPx}px`);

    const path = computePath(lineData);
    const pathWithPadding = computePathWithPadding(lineData);
    svgLine.setAttribute('d', pathWithPadding);
    markerStart.setAttribute('d', path);
    markerEnd.setAttribute('d', path);
  }
};

export const QuarterCurve = ({
  widthPx,
  heightPx,
  id,
  strokeColor,
  strokeWidth,
  strokeDashType,
  startArrowStyle,
  endArrowStyle,
  isReadOnly,
  style,
}: LineProps) => {
  const pathWithPadding = useMemo(() => {
    return computePathWithPadding({ widthPx, heightPx, strokeWidth, startArrowStyle, endArrowStyle });
  }, [widthPx, heightPx, strokeWidth, startArrowStyle, endArrowStyle]);

  const path = useMemo(() => computePath({ widthPx, heightPx, strokeWidth }), [widthPx, heightPx, strokeWidth]);
  const { svgWidth, svgHeight, offset } = useLineIconRender({
    isReadOnly: !!isReadOnly,
    widthPx,
    heightPx,
    strokeWidth,
  });

  const pathId = generateLineIconPathId(id, ID_PREFIX);
  const markerId = generateLineIconMarkerId(id, ID_PREFIX);
  const readOnlyPrefix = isReadOnly ? 'readonly-' : '';
  return (
    <svg
      id={`${readOnlyPrefix}${generateLineSvgId(id)}`}
      xmlns='http://www.w3.org/2000/svg'
      width={`${svgWidth}px`}
      height={`${svgHeight}px`}
      style={{
        position: 'absolute',
        overflow: 'visible',
        top: offset ? `${0 - offset}px` : '',
        left: offset ? `${0 - offset}px` : '',
        ...style,
      }}
    >
      <g transform={offset ? `translate(${offset},${offset})` : ''}>
        <LineMarkerDefs
          startArrowStyle={startArrowStyle}
          endArrowStyle={endArrowStyle}
          strokeColor={strokeColor}
          id={`${readOnlyPrefix}${markerId}`}
        />
        <path
          d={pathWithPadding}
          strokeWidth={strokeWidth}
          stroke={strokeColor}
          strokeDasharray={getDashArray(strokeDashType, strokeWidth)}
          fill='none'
          strokeLinecap='round'
          id={`${readOnlyPrefix}${pathId}`}
        />
        <LineMarkers
          startArrowStyle={startArrowStyle}
          endArrowStyle={endArrowStyle}
          strokeWidth={strokeWidth}
          path={path}
          id={`${readOnlyPrefix}${markerId}`}
        />
      </g>
    </svg>
  );
};
